
import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, Calendar, Users, CheckCircle, AlertCircle, Smartphone, CheckSquare, TrendingUp } from "lucide-react";
import { useClients } from "@/hooks/useClients";
import { useClientStatuses } from "@/hooks/useClientStatuses";

export const TopStatusBar = () => {
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loginTime] = useState(new Date());
  
  const { data: clients = [] } = useClients();
  const { data: clientStatuses = [] } = useClientStatuses();

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Calculate working hours since login
  const workingHours = Math.floor((currentTime.getTime() - loginTime.getTime()) / (1000 * 60 * 60));
  const workingMinutes = Math.floor(((currentTime.getTime() - loginTime.getTime()) % (1000 * 60 * 60)) / (1000 * 60));

  // Calculate client statistics based on their status (only for statuses that should show in top nav)
  const clientStats = clientStatuses
    .filter(status => status.show_in_top_nav) // فقط الحالات المضبوطة لتظهر في القائمة العلوية
    .map(status => ({
      ...status,
      count: clients?.filter(client => client.status === status.status_key).length || 0
    }));

  const StatusContent = () => (
    <div className="flex flex-wrap items-center gap-4 text-sm">
      {/* Today's Date - Arabic & Gregorian */}
      <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-lg">
        <Calendar className="w-4 h-4 text-blue-600" />
        <span className="font-medium text-blue-800">
          {currentTime.toLocaleDateString('ar-SA', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </span>
        <span className="text-blue-600 mx-1">|</span>
        <span className="font-medium text-blue-800">
          {currentTime.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </span>
      </div>

      {/* Login Time */}
      <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-lg">
        <Clock className="w-4 h-4 text-green-600" />
        <span className="font-medium text-green-800">
          دخول: {loginTime.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {/* Working Hours */}
      <div className="flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-lg">
        <Clock className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800">
          ساعات العمل: {workingHours}:{workingMinutes.toString().padStart(2, '0')}
        </span>
      </div>

      {/* Client Status - Real Data */}
      {clientStats.map((status, index) => (
        <Badge 
          key={status.id} 
          variant="secondary" 
          className="text-white"
          style={{ backgroundColor: status.color }}
        >
          <Users className="w-3 h-3 mr-1" />
          {status.name_ar}: {status.count}
        </Badge>
      ))}
    </div>
  );

  return (
    <>
      {/* Desktop View */}
      <div className="hidden md:block bg-gradient-to-r from-slate-100 to-blue-50 border-b border-slate-200 px-4 py-2">
        <StatusContent />
      </div>

      {/* Mobile View */}
      <div className="block md:hidden bg-gradient-to-r from-slate-100 to-blue-50 border-b border-slate-200 px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMobileModalOpen(true)}
          className="w-full justify-start"
        >
          <Smartphone className="w-4 h-4 mr-2" />
          عرض معلومات اليوم
        </Button>
      </div>

      {/* Mobile Modal */}
      <Dialog open={isMobileModalOpen} onOpenChange={setIsMobileModalOpen}>
        <DialogContent className="max-w-sm" dir="rtl">
          <DialogHeader>
            <DialogTitle>معلومات اليوم</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <StatusContent />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
